import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Button from "../common/Button";
import { FORMS_CHANGED_EVENT } from "../../services/formUploadService";

// The "still reading this form" card: progress, Back to forms, and (for an
// Auto-detect run on an existing form) Cancel. It sits over the blurred designer
// while a form is locked, and is also the whole page for a form that has no
// designer to show. The lists refresh on their own, so it goes away when the run
// ends. Nothing navigates for the officer.
export const ProcessingCard: React.FC<{
  name?: string;
  backPath: string;
  // With these, the card follows the run's progress and offers Cancel.
  templateId?: string;
  apiBase?: string;
  // Replaces navigating to `backPath` (e.g. the staff portal closes its editor).
  onBack?: () => void;
}> = ({ name, backPath, templateId, apiBase = "/api/forms/templates", onBack }) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [cancellable, setCancellable] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!templateId) return;
    let stopped = false;
    const look = async () => {
      try {
        const { data } = await axios.get(`${apiBase}/${templateId}/auto-fields`);
        if (stopped) return;
        setCancellable(data?.status === "running" && data?.preview === true);
        setProgress(data?.status === "running" && data.total > 0 ? { done: data.done ?? 0, total: data.total } : null);
      } catch {
        // a blip: look again next time
      }
    };
    void look();
    const timer = setInterval(look, 3000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [templateId, apiBase]);

  const cancel = async () => {
    if (!templateId || cancelling) return;
    setCancelling(true);
    try {
      await axios.post(`${apiBase}/${templateId}/auto-fields/cancel`);
      toast.success("Auto-detect cancelled. The form is unchanged.");
      // Lists re-read the form; it is unlocked now, so the lock goes away.
      window.dispatchEvent(new Event(FORMS_CHANGED_EVENT));
    } catch (error) {
      console.error("Could not cancel auto-detect:", error);
      toast.error("Could not cancel. Try again.");
      setCancelling(false);
    }
  };

  return (
    <div className="mx-auto max-w-md rounded-xl border border-base-300 bg-base-100 p-8 text-center shadow-xl">
      <span className="loading loading-spinner loading-md text-blue-500" />
      <h3 className="mt-4 font-semibold text-base-content">{name ? `Still reading "${name}"` : "Still reading this form"}</h3>
      <p className="mt-2 text-sm text-base-content/60">
        We are finding and grouping its fields.
        {progress ? ` (${progress.done} of ${progress.total} steps done.)` : ""} The form is locked until that is done. You
        can leave and come back; it shows as processing in your forms list.
      </p>
      <div className="mt-5 flex items-center justify-center gap-2">
        <Button variant="secondary" size="sm" onClick={() => (onBack ? onBack() : navigate(backPath))}>
          Back to forms
        </Button>
        {cancellable && (
          <Button
            variant="outline"
            size="sm"
            className="!border-red-200 dark:!border-red-900 !text-red-600 dark:!text-red-400 hover:!bg-red-50 dark:hover:!bg-red-950/40"
            disabled={cancelling}
            onClick={cancel}
          >
            {cancelling ? "Cancelling..." : "Cancel auto-detect"}
          </Button>
        )}
      </div>
    </div>
  );
};

// The card as a whole page (the detail page of a locked form).
const TemplateProcessingNotice: React.FC<{
  name?: string;
  backPath: string;
  embedded?: boolean;
  templateId?: string;
  apiBase?: string;
}> = ({ embedded, ...card }) => (
  <div className={embedded ? "py-16" : "flex-1 overflow-auto p-6"}>
    <ProcessingCard {...card} />
  </div>
);

export default TemplateProcessingNotice;
